<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useError } from "nuxt/app";

// フッターリンクの型定義
interface FooterLink {
	label: string;
	href: string;
}
const footerLinks: FooterLink[] = [
	{ label: "個人情報保護方針", href: "/info/privacy" },
	{ label: "勧誘方針", href: "/info/invite-policy" },
	{ label: "比較推奨方針", href: "/info/policy-sales" },
	{ label: "お客さま本位の業務運営に関する方針", href: "/info/customer" },
	{ label: "よくあるご質問", href: "/faq/" },
];

// エラーページ判定

const route = useRoute();

const isErrorPage = computed(() => {
	const error = useError();
	return !!error.value;
});
const isTermsPage = computed(() => {
	return route.path === "/info/terms";
});
const isPrivacyPolicyPage = computed(() => {
	return route.path === "/info/privacy-policy";
});
// 試算表ページ判定
const isCompare = computed(() => {
	return route.path.includes("/fire/compare");
});
</script>

<template>
<footer :class="{ 'footer-error': isErrorPage || isTermsPage || isPrivacyPolicyPage }">
        <!-- エラーページ、利用規約、プラポリの場合は版権のみ表示 -->
        <template v-if="isErrorPage || isTermsPage || isPrivacyPolicyPage">
            <div class="footer_copyright">
                Copyright © 2025 Bang Inc. All rights reserved.
            </div>
        </template>

        <!-- 通常ページの場合は全て表示 -->
        <template v-else>
            <!-- 通常footer -->
            <div class="footer footer-normal">
                <div class="footer-normal_company">
                    <!-- ロゴ部分 -->
                    <img src="../assets/images/logo.png" alt="ロゴ" class="footer-normal_logo" v-if="isCompare"/>
                    <!-- 会社情報 -->
                    <div class="footer-normal_company-info">
                        <div class="footer-normal_company-name">株式会社Bang</div>
                        <div class="footer-normal_company-address">
                            〒154-0024<br />
                            東京都世田谷区三軒茶屋2丁目11番22号4F
                        </div>
                    </div>
                    <!-- お知らせ -->
                    <div class="footer-normal_notice">
                        <p class="footer-normal_notice-text">
                            当社は、以下の損害保険会社の代理店として保険契約締結の媒介を行っており、締結代理権および告知受領権は有しておりません。
                        </p>
                        <div class="footer-normal_insurance">
                            <div class="footer-normal_insurance-title">保険会社</div>
                            <ul class="footer-normal_insurance-list">
                                <li>・ジェイアイ傷害火災保険株式会社</li>
                                <li>・ソニー損害保険株式会社</li>
                                <li>・損害保険ジャパン株式会社</li>
                                <li>・チューリッヒ保険会社</li>
                                <li>・日新火災海上保険株式会社</li>
                                <li>・三井住友海上火災保険株式会社</li>
                            </ul>
                            <div class="footer-normal_insurance-note">
                                ※保険商品に関するご照会は、各社申込みサイトの問い合わせ先よりご連絡ください。
                            </div>
                        </div>
                    </div>
                </div>
                <NavList variant="footer" />
                <!-- 下部リンク -->
                <ul class="footer-normal_links">
                    <li v-for="item in footerLinks" :key="item.label">
                        <a :href="item.href" :target="isCompare ? '_blank' : undefined">{{ item.label }}</a>
                    </li>
                </ul>
            </div>
            <!-- コピーライト -->
            <div class="footer_copyright">
                Copyright © 2025 Bang Inc. All rights reserved.
            </div>
        </template>
    </footer>
</template>

<style scoped lang="scss">
// フッター全体
footer {
    border-radius: 1.5rem 1.5rem 0 0; // 24px 24px 0 0
    background: linear-gradient(90deg, #EFF7FF 0%, #F0FFFF 100%);
}

// エラーページ用フッター
.footer-error {
    .footer_copyright {
        border-radius: 1.5rem 1.5rem 0 0; // 24px 24px 0 0
        background: linear-gradient(90deg, #EFF7FF 0%, #F0FFFF 100%);
        padding: 0.5rem 0;
        margin: unset;
    }
}

// 通常フッターのスタイル
.footer-normal {
    padding: 2.5rem 0 1.5rem;
    width: min(91vw, 30rem);
    margin: auto;

    // ロゴ
    &_logo {
        width: auto;
        height: 2rem; // 32px
        margin-bottom: 1.5rem;
    }
}

// 会社情報
.footer-normal_company {

    &-name {
        font-size: 1rem;
        font-weight: 700;
        margin-bottom: 0.25rem;
        color: #222;
    }

    &-address {
        font-size: 0.875rem;
        color: #222;
        line-height: 1.5;
    }
}

// お知らせ
.footer-normal_notice{
    color: #727268;
    font-size: 0.75rem;
    margin-top: 1rem;
}

.footer-normal_insurance{
    margin-top: 0.5rem;
    &-title {
        font-weight: 700;
    }
    &-note{
        margin-top: 0.25rem;
    }
}

// お問い合わせ
.footer-normal_contact {
    display: flex;
    align-items: center;
    border-top: 2px solid;
    border-bottom: 2px solid;
    border-image: linear-gradient(90deg, #2174C0 0%, #059696 100%)1;
    margin: 1.5rem auto;
    padding: 0.75rem 0;

    &-label {
        font-size: 1rem;
        color: #222;
        flex: 1 1 auto;
    }

    &-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.5rem;
        height: 1.5rem;
        border: 0.0625rem solid #2174C0;
        border-radius: 50%;
        margin-left: 0.5rem;
    }
}

// 下部リンク
.footer-normal_links {
    li {
        font-size: 0.75rem;
        color: #222;
        margin-bottom: 1rem;

        &:last-child {
            margin-bottom: 0;
        }

        // aタグのリセット
        a {
            color: #222;
            text-decoration: none; // 下線消す
            position: relative;

            &:hover {
                background: linear-gradient(90deg, #2174C0 0%, #059696 100%);
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                background-clip: text;
            }

            &::after {
                content: '';
                position: absolute;
                bottom: -0.0625rem;
                left: 0;
                width: 100%;
                height: 0.0625rem;
                background: transparent;
                transition: all 0.3s ease;
            }

            &:hover::after {
                background: linear-gradient(90deg, #2174C0 0%, #059696 100%);
            }

            &:visited,
            &:hover,
            &:active,
            &:focus {
                color: #222; // どの状態でも同じ色
                text-decoration: none;
            }
        }
    }
}

// コピーライト（通常ページ用）
.footer_copyright {
    text-align: center;
    font-size: 0.75rem;
    color: #1a5d9a;
    margin: 1rem auto 1.5rem;
}

// PC用のスタイル
@include mixin.media(pc) {
    .footer-normal {
        width: min(82vw, 64.5rem);
        margin: 0 auto;
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;

        // ロゴ
        &_logo {
            height: 2.5rem; // 40px
            width: auto;
            margin-right: 2rem;
        }

        .footer-normal_company {
            display: flex;
            flex-wrap: wrap;
            flex: 0 0 64%;
        }

        .footer-normal_notice{
            flex: 0 0 84%;
        }

        .footer-normal_insurance{
            &-list{
                display: flex;
                flex-wrap: wrap;
                li{
                    width: 248px;
                }
            }
        }

        .nav-list_footer {
            flex: 0 0 15%;
        }

        .footer-normal_links {
            flex: 0 0 21%;
        }
    }

    // エラーページ用PC スタイル
    .footer-error {
        .footer_copyright {
            padding: 0.75rem; // 12px
        }
    }
}
</style>
